import { createAsyncThunk } from '@reduxjs/toolkit';
import type { CommonRootState } from '@common/types/common-root-state-type';
import { selectUser } from '@common-reducers/UserSelectors';
import { backendApi } from '@common/reducers/backend-api/backend-api';
import { reportEngineConnected } from '@extension/wa-communication-layer/api/api-calls';
import { getWaMeInfoTool } from './WhatsappThunks';
import { ClientType, getClientType } from '@common-services/detect-context';

// Deduplicate only acknowledged reports, independently for each signed-in user.
const reported = new Set<string>();
const pending = new Set<string>();
// The backend does not read the request body, so failures are only visible in this
// console: warn once per reason.
const warned = new Set<string>();

function noteFailure(reason: string, detail?: unknown) {
  if (warned.has(reason)) return;
  warned.add(reason);
  console.warn('[reportEngineConnected] not acknowledged:', reason, detail);
}

// `WhatsAppReducer.me` is only populated by the page-world `me` event, which nothing emits
// any more (the last getMe() caller was removed in 962bf11a6). Fall back to the phone the
// engine itself reports so the connect signal does not depend on that event.
async function resolveEngineId(state: CommonRootState): Promise<string | undefined> {
  const fromMe = state.WhatsAppReducer.me?.id?._serialized ?? state.WhatsAppReducer.me?._id;
  if (fromMe) return fromMe;
  try {
    const res = await getWaMeInfoTool();
    if (res.success && res.data?.phone) return `${res.data.phone}@c.us`;
    noteFailure('wa-me-info-empty', res.success ? undefined : res.error);
  } catch (err) {
    noteFailure('wa-me-info-threw', err instanceof Error ? err.message : String(err));
  }
  return undefined;
}

/** Retry delay for the header's report loop: 30s doubling to a 5 min cap; `undefined` = stop. A user
 *  parked on the QR/loading screen must not make a page call every 30s forever. */
export const REPORT_MAX_ATTEMPTS = 12;
export function nextReportRetryDelayMs(attempt: number): number | undefined {
  if (attempt >= REPORT_MAX_ATTEMPTS) return undefined;
  return Math.min(30_000 * 2 ** attempt, 5 * 60_000);
}

export const reportEngineConnectedOnceThunk = createAsyncThunk<
  boolean,
  void,
  { state: CommonRootState }
>('reportEngineConnectedOnceThunk', async (_arg, thunkApi) => {
  // Chrome-gateway pods run this same extension, but the backend records any report as
  // engine:'extension'. A gateway is not an extension connection: report nothing (true = nothing
  // left to retry).
  if (getClientType() === ClientType.GATEWAY) return true;
  const state = thunkApi.getState();
  const userId = selectUser(state)?._id;
  if (!userId) return false;
  const engineId = await resolveEngineId(state);
  if (!engineId) return false;
  const key = JSON.stringify([userId, engineId]);
  if (reported.has(key)) return true;
  if (pending.has(key)) return false;
  pending.add(key);
  try {
    const response = await reportEngineConnected();
    if (!response.success) {
      noteFailure('api-not-success');
      return false;
    }
    reported.add(key);
    // Do not refresh an account that replaced this one during the request.
    if (selectUser(thunkApi.getState())?._id === userId) {
      thunkApi.dispatch(backendApi.endpoints.getFirstWeekChecklist.initiate(undefined, {
        forceRefetch: true, subscribe: false,
      }));
    }
    return true;
  } catch (err) {
    // The mounted header retries; reporting must never interrupt WhatsApp.
    noteFailure('api-threw', err instanceof Error ? err.message : String(err));
    return false;
  } finally {
    pending.delete(key);
  }
});
